import React, { useState, useEffect, useRef, useContext } from 'react';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/GetApp';

import SearchBar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import { HistoryService, historyFields } from '../../../data/reports-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import Helpers from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';
import {LoadingOverlay, NoRowsOverlay} from '../../../components/xgrid/overlays';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
}));

const Reports = () => {
    const classes = useStyles();
    const theme = useTheme();
    const downloadToastRef = useRef(null);
    const { height } = useWindowSize();
    const [history, setHistory] = useState([]);
    const [historyInit, setHistoryInit] = useState(false);
    const [, setHistoryTable] = useState(false);
    const [deleteHistoryDialog, setDeleteHistoryDialog] = useState(false);
    const [deleteAllHistoryDialog, setDeleteAllHistoryDialog] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [downloadHistoryDialog, setDownloadHistoryDialog] = useState(false);
    const historyTableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [historyColumnWidth, setHistoryColumnWidth] = useState({});
    const alertPerm = Auth.permissions.get('Alerts').write;
    const metadataPerm = Auth.permissions.get('Metadata').write;
    const sensorPerm = Auth.permissions.get('Sensors').write;
    const auditPerm = Auth.permissions.get('Audit').write;
    const suriPerm = Auth.permissions.get('SuricataRulesPolicy').write;
    const reportPerms = Auth.permissions.get('Reports');

    // Search state
    const [historySearch, setHistorySearch] = useState('');

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getHistory();

        setHistoryColumnWidth(getProfile('reports').historyWidths || {});
    }, []);

    useEffect(() => {
        setHistoryTable(true);
    }, [history]);

    const handleHistoryColumnResize = params => {
        const widths = historyTableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('reports', {
            ...getProfile('reports'),
            historyWidths: widths,
        });
        setHistoryColumnWidth(widths);
    };

    const handleHistoryColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = historyTableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('reports', {
                ...getProfile('reports'),
                historyColumns: order,
            });
        });
    };

    const getHistory = (search) => {
        HistoryService.getHistory(search).then(res => {
            setHistory(res.data);
            setHistoryTable(true);
        });
    };

    /* eslint-disable react/display-name */
    const tableRenderers = {
        created: d => {
            const date = Moment(d.row.created);
            return date.isValid() ? date.format(theme.date.long) : '';
        },
        finished: d => {
            const date = Moment(d.row.finished && d.row.finished.Time);
            return date.isValid() ? date.format(theme.date.long) : '';
        },
        status: d => { return <div>{d.row.data.status} - {Helpers.formatBytes(d.row.data.size || 0)}</div> },
        report: d => { return <div>{(d.row.report_data && d.row.report_data.title) || (d.row.data && d.row.data.title)}</div> },
        delivered_to: d => {
            const rec = _get(d.row, 'report_data.recipients', []).map((v, i) => {
                return <span key={'report-recipient-' + i}>{v}</span>;
            });

            if (rec.length) {
                return (
                    <div>
                        {rec.slice(1).reduce((xs, x, i) => {
                            return xs.concat([', ', x]);
                        }, [rec[0]])}
                    </div>
                );
            } else {
                return (<div>(none, build only)</div>);
            }
        },
    };

    const historyColumns = historyFields.map(f => {
        if (MC2CHECK) {
            if (f.variable !== 'delivered_to' && f.variable !== 'report') {
                return {
                    ...f,
                    width: historyColumnWidth[f.headerName] || 0,
                    ...(!historyColumnWidth[f.headerName] ? { flex: 1 } : {}),
                    valueGetter: d => _get(d, `row.${f.field}`, ''),
                    renderCell: tableRenderers[f.variable],
                };
            }
        } else {
            return {
                ...f,
                width: historyColumnWidth[f.headerName] || 0,
                ...(!historyColumnWidth[f.headerName] ? { flex: 1 } : {}),
                valueGetter: d => _get(d, `row.${f.field}`, ''),
                renderCell: tableRenderers[f.variable],
            };
        }
    }).filter(f => Boolean(f));

    const handleSelectHistory = (s) => {
        setSelectedHistory(s.row);
    };

    const handleHistorySearchChange = ({ search }) => {
        setHistorySearch(search);
    };

    const canDelete = report => {
        const typ = report.report_data ? report.report_data.id : report.data.id;
        switch (typ) {
            case 'alerts': return alertPerm;
            case 'metadata': return metadataPerm;
            case 'sensors': return sensorPerm;
            case 'audit': return auditPerm;
            case 'suricy': return suriPerm;
            default: return false;
        }
    };

    const handleCloseDeleteHistoryDialog = () => {
        setDeleteHistoryDialog(false);
        setTimeout(() => {
            setSelectedHistory(null);
        }, 1000);
    };

    const handleCloseDeleteAllHistoryDialog = () => {
        setDeleteAllHistoryDialog(false);
    };

    const handleCloseDownloadHistoryDialog = () => {
        setTimeout(() => {
            setSelectedHistory(null);
        }, 1000);
        setDownloadHistoryDialog(false);
    };

    const handleOpenDeleteHistory = () => {
        setDeleteHistoryDialog(true);
    };

    const handleOpenDeleteAllDialog = () => {
        setDeleteAllHistoryDialog(true);
    };

    const handleOpenDownloadHistoryDialog = () => {
        setDownloadHistoryDialog(true);
    };

    const handleDeleteHistory = () => {
        HistoryService.deleteHistory(selectedHistory.report_uuid, selectedHistory.id).then(() => {
            toast.success('Report deleted from History');
            setHistoryTable(false);
            setSelectedHistory(null);
            setDeleteHistoryDialog(false);
            getHistory(historySearch);
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
            setSelectedHistory(null);
            setDeleteHistoryDialog(false);
        });
    };

    const handleDownloadHistory = (format, created) => {
        HistoryService.downloadReportFromHistory(selectedHistory.report_uuid, selectedHistory.id, format).then((res) => {
            downloadToastRef.current = toast(<React.Fragment>
                <Box display="flex" alignItems="center">
                    <Typography>Report ready for download</Typography>
                    <Link
                        className={classes.linkRight}
                        target="_blank"
                        download={`audit-report-${created}.${format === 'rst' ? 'txt' : format}`}
                        href={res}
                    ><IconButton
                            aria-label="download history"
                            onClick={() => { toast.dismiss(downloadToastRef.current) }}
                            size="large"><DownloadIcon/></IconButton></Link>
                </Box>
            </React.Fragment>, {
                autoClose: 30000,
            });
        });
        handleCloseDownloadHistoryDialog();
    };

    const handleDeleteAllHistory = () => {
        HistoryService.deleteAllHistory().then(() => {
            toast.success('Report history deleted', {
                autoClose: 5000,
            });
            handleRefreshHistory();
        });
        handleCloseDeleteAllHistoryDialog();
    };

    const handleRefreshHistory = () => {
        setHistoryTable(false);
        getHistory(historySearch);
    };

    useEffect(() => {
        setHistoryTable(false);
        if (!historyInit) {
            setHistoryInit(true);
            return;
        }
        getHistory(historySearch);
    }, [historySearch]);

    const reportFormatType = (format) => {
        if (format === 'rst') {
            return 'Plain Text';
        }
        return format.toUpperCase();
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box mb={3} className={classes.fullWidth}>
                    <Box display="flex" justifyContent="space-between">
                        <SearchBar immediate onSearch={handleHistorySearchChange} searchservice={HistoryService}/>
                        <Box>
                            <Button variant="contained" className={classes.buttonRight} aria-label="refresh" onClick={handleRefreshHistory}><RefreshIcon/> Refresh</Button>
                            {reportPerms.delete &&
                                <Button variant="contained" className={classes.buttonRight} aria-label="delete all" onClick={handleOpenDeleteAllDialog}><DeleteIcon/> Delete All</Button>
                            }
                        </Box>
                    </Box>
                </Box>
                <Box className={classes.fullWidth} height={height - 310}>
                    <DataGridPro
                        apiRef={historyTableAPIRef}
                        components={{
                            Header: () => {
                                return (
                                    <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                        <IconButton
                                            disabled={!selectedHistory}
                                            variant="contained"
                                            color="primary"
                                            aria-label="download history"
                                            onClick={handleOpenDownloadHistoryDialog}
                                        >
                                        <DownloadIcon/>
                                        </IconButton>
                                        <IconButton
                                            disabled={!selectedHistory || !canDelete(selectedHistory)}
                                            variant="contained"
                                            color="secondary"
                                            aria-label="delete history"
                                            onClick={handleOpenDeleteHistory}
                                        >
                                        <DeleteIcon/>
                                        </IconButton>
                                    </Box>
                                );
                            },
                            LoadingOverlay:LoadingOverlay,
                            NoRowsOverlay:NoRowsOverlay
                        }}
                        rows={history}
                        // the issue was the value getters were wrong but masked by the correct table renderers
                        // so data was rendered correctly but sorting needs correct value
                        // programming is a fun and rewarding occupation
                        columns={(() => {
                            const p = getProfile('reports');
                            let c = historyColumns;

                            if (p.historyColumns && p.historyColumns.length) {
                                c = p.historyColumns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                });
                            }

                            return c;
                        })()}
                        disableMultipleSelection
                        disableMultipleColumnsFiltering
                        disableMultipleColumnsSorting
                        disableColumnMenu
                        onRowClick={handleSelectHistory}
                        sortingOrder={['desc', 'asc']}
                        sortModel={[{ field: 'created', sort: 'desc' }]}
                        onColumnResize={handleHistoryColumnResize}
                        onColumnOrderChange={handleHistoryColumnReorder}
                    />
                </Box>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteHistoryDialog}
                onOK={handleDeleteHistory}
                open={deleteHistoryDialog}
                message={`Are you sure you want to delete ${selectedHistory ? selectedHistory.report_data.title : ''} from the reports history?`}
                title="Delete History"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteAllHistoryDialog}
                onOK={handleDeleteAllHistory}
                open={deleteAllHistoryDialog}
                message={'Are you sure you want to delete all reports history?'}
                title="Delete All History"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDownloadHistoryDialog}
                onOK={handleDownloadHistory}
                open={downloadHistoryDialog}
                message={<React.Fragment>
                    {selectedHistory && Object.keys(selectedHistory.report_data.attachments).map((v, i) => {
                        if (selectedHistory.report_data.attachments[v]) {
                            return (
                                <Button
                                    key={'report-history-download-' + i}
                                    aria-label="download"
                                    onClick={() => { handleDownloadHistory(v, selectedHistory.created) }}>{reportFormatType(v)}
                                </Button>
                            );
                        }
                        return null;
                    })}
                </React.Fragment>}
                title="Download Report"
                cancelLabel="Cancel"
                nook
            />
        </Grid>
    );
};

export default Reports;
